/**
 * Slice 56 — keep what the job screens have already loaded on the phone, so closing or restarting the
 * app with no signal opens straight back on the same job (rooms, cover photos, checklist, counts...).
 */
import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";
import { idbGet, idbPut } from "@/lib/offline/idb";

const KEY = "query-cache-v1";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRY_CHARS = 300_000;
/** Money, messages and notifications are never copied to the phone's storage. */
const DENY = /invoice|payout|stripe|billing|connect|payment|message|thread|notification|payee/i;

type Saved = { key: string; userId: string; savedAt: number; state: ReturnType<typeof dehydrate> };

export async function startQueryPersistence(client: QueryClient, getUserId: () => Promise<string | null>) {
  const userId = await getUserId().catch(() => null);
  if (!userId) return;
  try {
    const saved = await idbGet<Saved>("kv", KEY);
    if (saved && saved.userId === userId && Date.now() - saved.savedAt < MAX_AGE_MS) hydrate(client, saved.state);
  } catch {
    /* start fresh */
  }
  let timer: ReturnType<typeof setTimeout> | null = null;
  const save = async () => {
    try {
      const state = dehydrate(client, {
        shouldDehydrateQuery: (q) => {
          if (q.state.status !== "success") return false;
          if (DENY.test(JSON.stringify(q.queryKey))) return false;
          try {
            return JSON.stringify(q.state.data ?? null).length < MAX_ENTRY_CHARS;
          } catch {
            return false;
          }
        },
      });
      await idbPut("kv", { key: KEY, userId, savedAt: Date.now(), state } satisfies Saved);
    } catch {
      /* storage full: the next change tries again */
    }
  };
  client.getQueryCache().subscribe(() => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void save(), 3000);
  });
}

/** Ask the browser to fetch these now so the service worker keeps them for no-signal use. */
export function warmPhotos(urls: (string | null | undefined)[]) {
  if (typeof window === "undefined") return;
  const seen = new Set<string>();
  for (const u of urls) {
    if (!u || seen.has(u) || u.startsWith("blob:") || u.startsWith("data:")) continue;
    seen.add(u);
    void fetch(u, { mode: "no-cors" }).catch(() => undefined);
  }
}
